import useFetch from "../hooks/useFetch";
import { v4 as uuidv4 } from "uuid";
import { useEffect, useRef, useState } from "react";

// Дефолтный контент из макета — используется, пока в CMS (collection work)
// нет новых полей Main/MainHover. Первая плашка каждой колонки показывает
// Main (что отдаём), по ховеру — расшифровку MainHover (зачем это бизнесу).
const DEFAULT_CARDS = [
  {
    id: "d1",
    Title: "Discovery",
    Main: "PRD",
    MainHover: "Что и зачем строим",
    Tags: [
      "Анализ рынка",
      "Глубинные интервью",
      "JTBD",
      "Ценностное предложение",
      "Бэклог гипотез",
    ],
  },
  {
    id: "d2",
    Title: "Delivery",
    Main: "MVP + Техническая документация",
    MainHover: "Рабочий продукт и документация по нему",
    Tags: ["UI/UX дизайн", "Архитектура", "Разработка/API", "OA", "Релиз"],
  },
  {
    id: "d3",
    Title: "Analysis",
    Main: "Решение",
    MainHover: "Масштабировать, отбросить или докрутить",
    Tags: [
      "Продуктовая аналитика",
      "A/B тесты",
      "Юнит-экономика (LTV/CAC)",
      "Воронки (AARRR)",
      "Retention/Когорты",
    ],
  },
];

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const smooth = (a, b, x) => {
  x = clamp((x - a) / (b - a), 0, 1);
  return x * x * (3 - 2 * x);
};

// Порог, ниже которого сцена со скроллом отключается и блок показывается
// статично (как есть, без пина/волны) — см. референс kattemera.github.io/its9it-test.
const STATIC_MODE_WIDTH = 960;

// window.innerWidth/matchMedia читаются синхронно — считаем режим сразу в
// инициализаторе useState, а не в эффекте, чтобы первый рендер уже был в
// правильном режиме (иначе на десктопе первый кадр — safe-статика, а через
// секунду JS переключает на pin-сцену: заметный скачок высоты/раскладки).
function computeIsStatic() {
  return (
    window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
    window.innerWidth <= STATIC_MODE_WIDTH
  );
}

export default function WorkSection() {
  // На ≤600 первую плашку раскрывают тапом — на тач-устройствах нет :hover.
  // Храним id карточек, которые сейчас "раскрыты" тапом (см. work-card__item--main.is-active в CSS).
  const [activeIds, setActiveIds] = useState({});
  const toggleActive = (id) =>
    setActiveIds((prev) => ({ ...prev, [id]: !prev[id] }));

  // Режим сцены (static/pin) — сразу верный на первом рендере, см. computeIsStatic.
  const [isStatic, setIsStatic] = useState(computeIsStatic);

  const worksFetch = useFetch(`${window.$host}/api/works?populate=*`);

  // Берём контент из CMS только когда там заполнены новые поля (Main/MainHover),
  // иначе показываем дефолт из макета — старые записи под новый дизайн не подходят.
  const cmsRaw =
    !worksFetch.loading && !worksFetch.error && worksFetch.data
      ? worksFetch.data.data
      : null;

  const cmsCards =
    cmsRaw &&
    cmsRaw.length >= 3 &&
    cmsRaw.slice(0, 3).every((card) => card.Main && card.MainHover)
      ? cmsRaw.slice(0, 3).map((card) => ({
          id: card.id,
          Title: card.Title,
          Main: card.Main,
          MainHover: card.MainHover,
          Tags: (card.List?.[0]?.children || []).map(
            (item) => item.children[0].text
          ),
        }))
      : null;

  const cards = cmsCards || DEFAULT_CARDS;

  const renderCard = (card) => (
    <li className="work-card" key={card.id}>
      <h3 className="title-4 work-card__title">{card.Title}</h3>
      <ul className="work-card__list">
        <li
          className={
            "work-card__item work-card__item--main" +
            (activeIds[card.id] ? " is-active" : "")
          }
          onClick={() => toggleActive(card.id)}
        >
          <span className="work-card__main-default">{card.Main}</span>
          <span className="work-card__main-hover" aria-hidden="true">
            {card.MainHover}
          </span>
        </li>
        {card.Tags.map((tag) => (
          <li key={uuidv4()} className="work-card__item">
            {tag}
          </li>
        ))}
      </ul>
    </li>
  );

  // ---- скролл-сцена: заголовок выезжает, карточки притухают, рамка
  // "дорисовывается" пунктиром, поверх проявляется переливающаяся волна
  // точек, последними — подписи волны. Сцена высотой в экран (CSS —
  // .work-scene); весь сценарий запускается одним скроллом и идёт по
  // таймеру, см. play(). ----
  //
  // Тонкая настройка сцены (sizeStage/apply — измерения, зависящие от
  // раскладки шрифтов/контента) по-прежнему отложена до window "load", но
  // САМ РЕЖИМ (static/pin) синхронный: он определяется только
  // window.innerWidth/matchMedia (см. computeIsStatic), поэтому вычисляется
  // прямо в инициализаторе useState — первый рендер сразу в верном режиме,
  // без промежуточного "safe-режим -> переключение JS-ом" скачка раскладки.

  const sceneRef = useRef(null);
  const pinBoxRef = useRef(null);
  const leadRef = useRef(null);
  const gridWrapRef = useRef(null);
  const canvasRef = useRef(null);
  const drawRectRef = useRef(null);
  const dotRectRef = useRef(null);
  const frameSvgRef = useRef(null);

  const waveStateRef = useRef({ opacity: 0, amp: 0 });
  const boxRef = useRef({ w: 0, h: 0 });
  const shiftUpRef = useRef(0);
  const isStaticRef = useRef(isStatic);

  useEffect(() => {
    const scene = sceneRef.current;
    const pinBox = pinBoxRef.current;
    const lead = leadRef.current;
    const gridWrap = gridWrapRef.current;
    const canvas = canvasRef.current;
    const drawRect = drawRectRef.current;
    const dotRect = dotRectRef.current;
    const frameSvg = frameSvgRef.current;
    if (!scene || !pinBox || !lead || !gridWrap || !canvas || !frameSvg) return;

    const ctx = canvas.getContext("2d");
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    function sizeStage() {
      const pad = 14;
      const w = gridWrap.clientWidth + pad * 2;
      const h = gridWrap.clientHeight + pad * 2;
      boxRef.current = { w, h };

      // сетка карточек стоит по центру при загрузке, затем "опускается" на
      // эту величину по мере затухания (см. apply)
      shiftUpRef.current = (lead.offsetHeight + 100) / 2;

      frameSvg.setAttribute("viewBox", `0 0 ${w} ${h}`);
      drawRect.setAttribute("width", w - 2);
      drawRect.setAttribute("height", h - 2);
      dotRect.setAttribute("width", w - 2);
      dotRect.setAttribute("height", h - 2);

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      if (!isStaticRef.current) {
        pinBox.style.transform = "none";
        const need = pinBox.offsetHeight;
        const avail = window.innerHeight;
        const s = Math.min(1, (avail - 24) / need);
        pinBox.style.transform = s < 1 ? `scale(${s})` : "none";
      } else {
        pinBox.style.transform = "none";
      }
    }

    function apply(p) {
      // Порядок бит: СНАЧАЛА fade-up заголовка (work-lead) + опускание сетки
      // на своё место, ПОТОМ анимация блока ниже (карточки притухают, рамка
      // дорисовывается, волна, подписи). Уезд вверх в финале — штатным
      // скроллом, как у всех секций (отдельной анимации нет).
      const head = smooth(0.0, 0.25, p);
      const dim = smooth(0.22, 0.44, p);
      const border = smooth(0.4, 0.6, p);
      const wave = smooth(0.56, 0.84, p);
      const labels = smooth(0.8, 0.96, p);

      scene.style.setProperty("--work-dim", dim.toFixed(4));
      scene.style.setProperty("--work-border", border.toFixed(4));
      scene.style.setProperty("--work-labels", labels.toFixed(4));
      drawRect.setAttribute("stroke-dashoffset", (100 * (1 - border)).toFixed(2));

      // Сетка опускается со стартового «поднятого» положения ВМЕСТЕ с
      // появлением заголовка (раньше было завязано на dim — из-за чего
      // карточки притухали первыми; теперь на head, чтобы заголовок шёл
      // первым и не накладывался на поднятые карточки).
      gridWrap.style.transform = `translateY(${(
        -shiftUpRef.current *
        (1 - head)
      ).toFixed(2)}px)`;

      lead.style.opacity = head.toFixed(4);
      lead.style.transform = `translateY(${(10 * (1 - head)).toFixed(2)}px)`;

      waveStateRef.current.opacity = wave;
      waveStateRef.current.amp = wave;
    }

    // ---- Пауза и запуск одним скроллом ----
    // Блок доезжает до верха экрана -> страница фиксируется на нём, этапы
    // стоят по центру без анимации (пауза, чтобы успеть прочитать).
    // Следующий скролл проигрывает анимацию по таймеру (PLAY_MS), скролл
    // на это время заблокирован. Ещё один скролл уводит блок наверх штатно.
    // Скролл вверх — зеркально: блок доехал сверху -> пауза в финальном виде
    // -> скролл вверх = анимация назад -> пауза на этапах -> блок уходит вниз.
    // Блок, целиком ушедший под нижний край экрана, сбрасывается в начало.
    const PLAY_MS = 2700;
    // минимальная пауза на этапах, чтобы инерция того же жеста, которым
    // доехали до блока, не запустила анимацию сразу
    const HOLD_MIN_MS = 600;
    // после анимации ждём, пока стихнет инерция колеса/тачпада, чтобы
    // хвост того же жеста не утащил блок сразу наверх
    const RELEASE_QUIET_MS = 250;
    // скролл считается пользовательским, если был жест не раньше этого
    // (плавная прокрутка из меню жестом не считается — её не прерываем)
    const USER_INPUT_MS = 800;

    let progress = 0;
    let state = "idle"; // idle | holding | playing | done | holdingEnd
    let prevTop = Infinity;
    let locked = false;
    let lockY = 0;
    let lastInput = -Infinity;
    let lastBlocked = 0;
    let unlockAt = 0;
    let rafPlay = 0;
    let unlockTimer = 0;
    let touchY = 0;

    const now = () => performance.now();
    const sceneTop = () => scene.getBoundingClientRect().top;
    // пока висит прелоадер, секции выше ещё пустые и блок стоит у верха
    // экрана — запускать анимацию под оверлеем нельзя
    const pageReady = () => !document.querySelector(".preloader");

    function finish() {
      state = "done";
      progress = 1;
      apply(1);
    }

    function tryUnlock() {
      const wait = Math.max(
        RELEASE_QUIET_MS - (now() - lastBlocked),
        unlockAt - now()
      );
      if (wait > 0) {
        unlockTimer = window.setTimeout(tryUnlock, wait);
        return;
      }
      locked = false;
    }

    function lockHere() {
      locked = true;
      lockY = Math.round(window.scrollY + sceneTop());
      window.scrollTo({ top: lockY, behavior: "instant" });
      prevTop = 0;
    }

    function hold() {
      state = "holding";
      lockHere();
      unlockAt = now() + HOLD_MIN_MS;
      tryUnlock();
    }

    // пауза в финальном виде, когда вернулись к блоку скроллом вверх
    function holdEnd() {
      state = "holdingEnd";
      lockHere();
      unlockAt = now() + HOLD_MIN_MS;
      tryUnlock();
    }

    // target: 1 — анимация вперёд, 0 — назад
    function play(target) {
      state = "playing";
      lockHere();
      unlockAt = 0;
      const from = progress;
      const dur = PLAY_MS * Math.abs(target - from);
      const start = now();
      const step = (t) => {
        const k = dur > 0 ? clamp((t - start) / dur, 0, 1) : 1;
        progress = from + (target - from) * k;
        apply(progress);
        if (k < 1) {
          rafPlay = requestAnimationFrame(step);
        } else {
          state = target === 1 ? "done" : "holding";
          tryUnlock();
        }
      };
      rafPlay = requestAnimationFrame(step);
    }

    // общий обработчик жеста; down — жест ведёт страницу вниз
    function onGesture(e, down) {
      if (isStaticRef.current) return;
      if (locked) {
        e.preventDefault();
        lastBlocked = now();
        return;
      }
      lastInput = now();
      // блок стоит у верха (пауза на этапах или пришли из меню) —
      // этот скролл запускает анимацию
      if (!pageReady() || Math.abs(sceneTop()) > 2) return;
      if ((state === "idle" || state === "holding") && down) {
        e.preventDefault();
        lastBlocked = now();
        play(1);
      } else if ((state === "done" || state === "holdingEnd") && !down) {
        // скролл вверх на финальном виде — анимация назад
        e.preventDefault();
        lastBlocked = now();
        play(0);
      }
    }

    function onScroll() {
      if (isStaticRef.current) return;
      if (locked) {
        // гасим инерцию тача / перетаскивание скроллбара
        if (Math.abs(window.scrollY - lockY) > 1) {
          window.scrollTo({ top: lockY, behavior: "instant" });
        }
        return;
      }
      const top = sceneTop();
      const wasTop = prevTop;
      prevTop = top;
      // ушли с паузы обратно вверх — при следующем подъезде пауза снова
      if (state === "holding" && top > 2) state = "idle";
      // ушли с финальной паузы вниз — блок уезжает наверх штатно
      if (state === "holdingEnd" && top < -2) state = "done";
      if (state === "done" && pageReady()) {
        if (top >= window.innerHeight) {
          // блок целиком ушёл под экран — сброс, при подъезде сыграет заново
          state = "idle";
          progress = 0;
          apply(0);
        } else if (
          wasTop < 0 &&
          top >= 0 &&
          top < window.innerHeight * 0.6 &&
          now() - lastInput < USER_INPUT_MS
        ) {
          // вернулись к блоку скроллом вверх — пауза в финальном виде
          holdEnd();
        }
        return;
      }
      if (state !== "idle" || !pageReady()) return;
      if (top > 0) return;
      if (top <= -window.innerHeight * 0.6) {
        // блок пролистали (перезагрузка ниже, переход из меню) — сразу финал
        finish();
      } else if (now() - lastInput < USER_INPUT_MS) {
        hold();
      }
    }

    const onWheel = (e) => onGesture(e, e.deltaY > 0);
    const onTouchStart = (e) => {
      touchY = e.touches[0].clientY;
    };
    const onTouchMove = (e) => {
      const y = e.touches[0].clientY;
      onGesture(e, touchY - y > 0);
      touchY = y;
    };
    const DOWN_KEYS = ["ArrowDown", "PageDown", "End", " ", "Spacebar"];
    const SCROLL_KEYS = [...DOWN_KEYS, "ArrowUp", "PageUp", "Home"];
    const onKeyDown = (e) => {
      if (!SCROLL_KEYS.includes(e.key)) return;
      if (e.target.closest?.("input, textarea, select, [contenteditable]")) return;
      onGesture(e, DOWN_KEYS.includes(e.key) && !(e.key === " " && e.shiftKey));
    };

    // Волна — живая, перетекающая halftone-полутоновая заливка (техника из
    // референса kattemera.github.io/its9it-test): на каждый тик по сетке
    // считается интенсивность точки как пересечение текстуры (s1/s2) и двух
    // диагональных полос (b1/b2), центры полос медленно дрейфуют во времени
    // (c1/c2) — поэтому волна течёт, а не просто мерцает на месте. Диагональ
    // (снизу-слева наверх-направо) и общие пропорции подобраны под форму
    // дуги на макете.
    let rafDraw = 0;
    function drawWaves(now) {
      rafDraw = requestAnimationFrame(drawWaves);
      const { w: W, h: H } = boxRef.current;
      ctx.clearRect(0, 0, W, H);
      const { opacity: op, amp } = waveStateRef.current;
      if (op < 0.01 || W === 0) return;

      const t = now / 1000;
      const step = 14;

      ctx.fillStyle = "#0be9b3";
      for (let y = 5; y < H; y += step) {
        for (let x = 5; x < W; x += step) {
          const nx = x / W;
          const ny = y / H;
          const u = nx - (ny - 0.5) * 0.45;
          const s1 = Math.sin(u * 7.6 - t * 1.4);
          const s2 = Math.sin(nx * 4.2 + ny * 5.4 - t * 0.95 + 1.3);
          const v = (s1 * 0.62 + s2 * 0.38) * 0.5 + 0.5;

          const diag = nx * 0.62 + (1 - ny) * 0.9;
          const c1 = 0.3 + 0.3 * Math.sin(t * 0.32);
          const c2 = 0.88 + 0.26 * Math.sin(t * 0.26 + 2.1);
          const b1 = Math.exp(-Math.pow((diag - c1) / 0.22, 2));
          const b2 = Math.exp(-Math.pow((diag - c2) / 0.24, 2));
          const band = Math.min(1.35, b1 * 1.25 + b2 * 1.05);

          const inten = Math.pow(v, 1.9) * band * amp;
          if (inten < 0.05) continue;

          const r = 0.25 + inten * 2.15;
          ctx.globalAlpha = Math.min(1, inten * 2.0) * op;
          ctx.beginPath();
          ctx.arc(x, y, r, 0, 6.2832);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
    }

    function goStatic() {
      scene.style.setProperty("--work-dim", "0");
      gridWrap.style.transform = "";
      lead.style.opacity = "";
      lead.style.transform = "";
    }

    function evaluateMode() {
      const next = reducedMotion || window.innerWidth <= STATIC_MODE_WIDTH;
      isStaticRef.current = next;
      setIsStatic(next);
    }

    function refresh() {
      evaluateMode();
      sizeStage();
      if (isStaticRef.current) {
        goStatic();
      } else {
        apply(progress);
        onScroll();
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", refresh);
    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("keydown", onKeyDown);
    rafDraw = requestAnimationFrame(drawWaves);
    // ждём раскладки шрифтов/контента перед первым замером
    const loadTimer = window.setTimeout(refresh, 0);
    window.addEventListener("load", refresh);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", refresh);
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("load", refresh);
      window.clearTimeout(loadTimer);
      window.clearTimeout(unlockTimer);
      cancelAnimationFrame(rafPlay);
      cancelAnimationFrame(rafDraw);
    };
  }, []);

  return (
    <section className="work" id="work">
      <div
        className={"work-scene" + (isStatic ? " work-scene--static" : "")}
        ref={sceneRef}
      >
        <div className="work-scene__pin">
          <div className="container">
            <div className="content-box">
              <div className="work__box" ref={pinBoxRef}>
                <p className="work-lead" ref={leadRef}>
                  <span className="work-lead__white">
                    Вокруг продукта выстраиваем юридическую и финансовую архитектуру.{" "}
                  </span>
                  <span className="work-lead__gray">
                    Проектируем договорные модели, схемы расчётов и денежные потоки под его механику,{" "}
                  </span>
                  <span className="work-lead__white">
                    чтобы сделать бизнес-модель рабочей.
                  </span>
                </p>

                <div className="work-grid-wrap" ref={gridWrapRef}>
                  <div className="work-wave-labels" aria-hidden="true">
                    <span className="work-wave-labels__item work-wave-labels__item--left">
                      <span className="work-wave-labels__dot" />
                      Финансовые потоки
                    </span>
                    <span className="work-wave-labels__item work-wave-labels__item--right">
                      <span className="work-wave-labels__dot" />
                      Юридический контур
                    </span>
                  </div>

                  <canvas className="work-wave-canvas" ref={canvasRef} />

                  <svg
                    className="work-frame-svg"
                    ref={frameSvgRef}
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    <defs>
                      <mask id="work-draw-mask">
                        <rect
                          ref={drawRectRef}
                          x="1"
                          y="1"
                          fill="none"
                          stroke="#fff"
                          strokeWidth="6"
                          pathLength="100"
                          strokeDasharray="100"
                          strokeDashoffset="100"
                        />
                      </mask>
                    </defs>
                    <rect
                      ref={dotRectRef}
                      x="1"
                      y="1"
                      fill="none"
                      stroke="#98a2b3"
                      strokeWidth="1.4"
                      strokeDasharray="1.4 6.5"
                      strokeLinecap="round"
                      mask="url(#work-draw-mask)"
                    />
                  </svg>

                  <ul className="work__list">{cards.map(renderCard)}</ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
